import { CodeEditContextRefCode } from '@/shared/helpers/api.ts';
import { CodeEditFields } from '@/shared/codeedit/codeEditFields.ts';
import { evaluateReferenceFilter, predicateMatchesEverything, referenceFilterMismatchMessage } from '@/shared/codeedit/dynamicFilter.ts';
import { CaseSensitiveCodeRefs, ReferenceFilterNode } from '@/shared/codeedit/dynamicFilterTypes.ts';

export type CodeRefSelectorCode = CodeEditContextRefCode & {
    isSelectable?: boolean;
    unselectableReason?: string;
    // Left out of the option list, but still used to display the current value when it is selected
    isHidden?: boolean;
    // The code fields whose current values made the dynamic filter rule this option out
    failingFields?: string[];
};

// Dynamic filters

export type DynamicFilterPredicates = {
    field: string;
    predicate: ReferenceFilterNode;
    showFiltered?: boolean;
    caseSensitiveCodeRefs?: CaseSensitiveCodeRefs;
}[];

/*
Marks the options a dynamic filter rules out as not selectable, leaving the option itself untouched
when it still matches. Ruled-out options are also marked hidden unless the filter is set to show them greyed out.

CodeRefSelector re-filters and re-sorts its whole list whenever the array it is given changes,
so an array is only replaced when an option was actually marked, and the outer record only when at least one field changed.
*/
export function applyDynamicFilters(
    codes: Record<string, CodeRefSelectorCode[]>,
    predicates: DynamicFilterPredicates,
    codeFieldValues: CodeEditFields
): Record<string, CodeRefSelectorCode[]> {
    let updated: Record<string, CodeRefSelectorCode[]> | null = null;

    for (const { field, predicate, showFiltered, caseSensitiveCodeRefs } of predicates) {
        const options = codes[field];
        // Nothing to mark when the filter cannot rule anything out yet
        if (!options || predicateMatchesEverything(predicate, codeFieldValues)) {
            continue;
        }

        let anyUnselectable = false;
        const evaluated = options.map(option => {
            const { matched, failing } = evaluateReferenceFilter(predicate, option.dynamicFilterReferenceValues ?? null, codeFieldValues, caseSensitiveCodeRefs);
            if (matched) {
                return option;
            }
            anyUnselectable = true;
            return {
                ...option,
                isSelectable: false,
                unselectableReason: referenceFilterMismatchMessage(failing),
                isHidden: !showFiltered,
                failingFields: failing
            };
        });

        if (!anyUnselectable) {
            continue;
        }
        updated ??= { ...codes };
        updated[field] = evaluated;
    }

    return updated ?? codes;
}

// True when the dynamic filter has hidden every option that would otherwise be listed, leaving the picker empty
export function dynamicFilterHidesAllOptions(options?: CodeRefSelectorCode[]): boolean {
    if (!options || !options.some(option => option.isHidden)) {
        return false;
    }
    return options.every(option => option.isHidden || option.isFiltered);
}

// Every code field that ruled out at least one hidden option. Changing any of them may bring options back.
// Empty when only fixed values in the filter rule the options out.
export function dynamicFilterBlamedFields(options?: CodeRefSelectorCode[]): string[] {
    const hidden = (options ?? []).filter(option => option.isHidden);
    return [...new Set(hidden.flatMap(option => option.failingFields ?? []))];
}

export function noDynamicFilterMatchMessage(fieldLabels: string[], hint?: string): string {
    const message = fieldLabels.length > 0
        ? `No options match the dynamic filter for the current value of: ${fieldLabels.join(', ')}`
        : 'No options match the current dynamic filter';
    return hint ? `${message} (${hint})` : message;
}

// Filters

export type CodeRefFilterFunction = (refCode: CodeRefSelectorCode) => boolean;

export type CodeRefFilterFunctions<CodeType extends CodeEditFields> = {
    [Attribute in keyof CodeType]?: CodeRefFilterFunction;
};

// Validators

export type CodeRefValidationResult = {
    message: string;
    messageType: string;
    error: boolean;
};

export type CodeRefValidator = (modifiedCode: CodeEditFields, originalCode: CodeEditFields) => CodeRefValidationResult;

export type CodeRefValidators<CodeType extends CodeEditFields> = {
    [Attribute in keyof CodeType]?: CodeRefValidator;
};

// Change-handlers

export type CodeRefChangedHandler = (localCode: CodeEditFields) => void;

export type CodeRefChangedHandlers<CodeType extends CodeEditFields> = {
    [Attribute in keyof CodeType]?: CodeRefChangedHandler;
};
